<template>
  <permission-gate
    v-slot="{ canEdit }"
    moduleKey="colores"
  >
    <main-breadcrumb
      :items="items"
      showBackButton
    ></main-breadcrumb>
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Listado de colores</custom-title>
      </template>
      <template #right>
        <el-tooltip
          :disabled="canEdit"
          content="El usuario no posee permisos de edición"
          placement="top"
        >
          <el-button
            type="primary"
            size="default"
            :icon="IconAdd"
            :disabled="!canEdit"
            @click="newColor"
          >
            Nuevo color
          </el-button>
        </el-tooltip>
      </template>
    </main-buttons-bar>
    <colores-filters></colores-filters>
    <custom-table
      :requestApi="findAllColoresHTTP"
      :data="colores"
      :paramId="'Codigo'"
      :columns="columns"
      :showDetailButton="false"
      :showEditButton="false"
      :requestArgs="{ v: filtersVersion }"
    >
      <template #hex="{ row }">
        <span
          class="chip"
          :style="{ backgroundColor: row.Hex || '#ffffff' }"
          :title="row.Hex || ''"
        />
        {{ row.Hex }}
      </template>

      <template #custom-actions="{ row }">
        <el-tooltip
          :disabled="!row.EsSistema"
          content="Los colores del sistema no se pueden editar"
          placement="top"
        >
          <el-button
            :icon="IconEdit"
            :disabled="row.EsSistema || !canEdit"
            @click.prevent="openEditModal(row)"
          ></el-button>
        </el-tooltip>
        <el-tooltip
          :disabled="!row.EsSistema"
          content="Los colores del sistema no se pueden eliminar"
          placement="top"
        >
          <el-button
            :icon="IconDelete"
            type="danger"
            :disabled="row.EsSistema || !canEdit"
            @click.prevent="deleteColor(row)"
          ></el-button>
        </el-tooltip>
      </template>
    </custom-table>

    <colores-modal
      v-model:visible="modalVisible"
      :form="coloresStore.color"
      :saving="saving"
      @update:form="(value) => (coloresStore.color = value)"
      @save="onSave"
    />
  </permission-gate>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import { useColoresStore } from '../stores/colores_store.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTable from '@/shared/components/custom_table.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import ColoresFilters from './colores_filters.vue';
import ColoresModal from './colores_modal.vue';
import PermissionGate from '@/shared/components/permission_gate.vue';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import IconAdd from '~icons/mdi/add';
import IconEdit from '~icons/mdi/edit';
import IconDelete from '~icons/mdi/delete';

const coloresStore = useColoresStore();
const {
  firstColores,
  resetColor,
  findAllColoresHTTP,
  setPage,
  emptyList,
  resetFilters,
  deleteColorHTTP,
  saveColor,
} = coloresStore;
const { colores, filtersVersion } = storeToRefs(coloresStore);

const modalVisible = ref(false);
const saving = ref(false);

const items = [
  { label: 'Inicio', to: '/dashboard', replace: false },
  { label: 'Listado', to: null, replace: false },
];

const columns = [
  { field: 'Descripcion', label: 'Descripción', width: 360, sortable: true, isVisible: true },
  { field: 'Pantone', label: 'Pantone', width: 280, sortable: false, isVisible: true },
  { field: 'Hex', label: 'Hex', width: 240, sortable: false, isVisible: true, slot: 'hex' },
];

function newColor() {
  resetColor();
  modalVisible.value = true;
}

function openEditModal(row) {
  if (row.EsSistema) return;
  coloresStore.color = { ...row };
  modalVisible.value = true;
}

async function deleteColor(row) {
  if (row.EsSistema) return;
  try {
    await ElMessageBox.confirm(
      `¿Desea eliminar el color "${row.Descripcion}"?`,
      'Confirmar eliminación',
      {
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        type: 'warning',
      },
    );
  } catch (error) {
    return;
  }

  try {
    useLoading().openLoading();
    await deleteColorHTTP(row);
    useMessageSuccess().open('El color se ha eliminado correctamente.');
  } catch (err) {
    useMessageError().open('Ha ocurrido un error al intentar eliminar el color.');
  } finally {
    useLoading().closeLoading();
  }
}

async function onSave() {
  try {
    saving.value = true;
    useLoading().openLoading();
    await saveColor();
    useMessageSuccess().open('Los datos se han guardado exitosamente!');
    modalVisible.value = false;
    resetColor();
    setPage(1);
    emptyList();
    await findAllColoresHTTP();
  } catch (err) {
    useMessageError().open('Ha ocurrido un error al intentar guardar el color.');
  } finally {
    saving.value = false;
    useLoading().closeLoading();
  }
}

onBeforeMount(async () => {
  resetFilters();
  await firstColores();
});
</script>

<style scoped>
.chip {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 6px;
  border: 1px solid #dcdfe6;
  vertical-align: middle;
}
</style>
